import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Loader2, Printer, Download, Copy, ListOrdered, FileDown } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { contractService } from '@/services/contractService'
import { employeesService } from '@/services/employeesService'
import { getSalaryByMonth, getPayrollTest, generatePayroll, getSalaryById, toApiMonthYear } from '@/services/payrollService'
import type {
  SalaryByMonthItem,
  PayrollTestItem,
  GeneratePayrollPayload,
  SalaryById,
} from '@/api/types/payroll.types'
import type { EmployeeOption } from '@/api/types'
import { getLogoPath } from '@/components/shared/Logo'
import { PaySlipModal } from '@/modules/hr/components/PaySlipModal'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function getStartEndForMonth(ym: string): { startDate: string; endDate: string } {
  if (!ym || ym.length < 7) {
    const d = new Date()
    const y = d.getFullYear()
    const m = d.getMonth()
    const first = new Date(y, m, 1)
    const last = new Date(y, m + 1, 0)
    return {
      startDate: first.toISOString().slice(0, 10),
      endDate: last.toISOString().slice(0, 10),
    }
  }
  const [y, m] = ym.split('-').map(Number)
  const first = new Date(y, m - 1, 1)
  const last = new Date(y, m, 0)
  return {
    startDate: first.toISOString().slice(0, 10),
    endDate: last.toISOString().slice(0, 10),
  }
}

/** Display month/year (locale-aware). Accepts YYYY-MM or MM-YYYY. */
function formatMonthYearLabel(ym?: string, locale?: string): string {
  if (!ym || ym.length < 6) return '—'
  const parts = ym.split('-')
  let monthNum: number
  let yearStr: string
  if (parts.length >= 2 && parts[0].length === 4) {
    yearStr = parts[0]
    monthNum = parseInt(parts[1], 10)
  } else if (parts.length >= 2 && parts[1].length === 4) {
    monthNum = parseInt(parts[0], 10)
    yearStr = parts[1]
  } else {
    return ym
  }
  if (monthNum >= 1 && monthNum <= 12) {
    const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
    const d = new Date(Number(yearStr), monthNum - 1, 1)
    return d.toLocaleDateString(loc, { month: 'long', year: 'numeric' })
  }
  return ym
}

function formatDate(d?: string | null, locale?: string): string {
  if (!d) return '—'
  try {
    const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
    return new Date(d).toLocaleDateString(loc, { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return String(d).slice(0, 10)
  }
}

function formatNum(n?: number | null): string {
  if (n == null) return '—'
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export const Payroll: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [monthYear, setMonthYear] = useState(getDefaultMonthYear)
  const defaultRange = useMemo(() => getStartEndForMonth(monthYear), [monthYear])
  const [startDate, setStartDate] = useState(defaultRange.startDate)
  const [endDate, setEndDate] = useState(defaultRange.endDate)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [salaryList, setSalaryList] = useState<SalaryByMonthItem[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [tested, setTested] = useState(false)
  const [testList, setTestList] = useState<PayrollTestItem[]>([])
  const [, setTestTotalRecords] = useState(0)
  const [testLoading, setTestLoading] = useState(false)
  const [posting, setPosting] = useState(false)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [paySlipOpen, setPaySlipOpen] = useState(false)
  const [paySlipData, setPaySlipData] = useState<SalaryById | null>(null)
  const [paySlipLoading, setPaySlipLoading] = useState(false)
  const [printListLoading, setPrintListLoading] = useState(false)
  const [excelListLoading, setExcelListLoading] = useState(false)

  const hasSalaryData = salaryList.length > 0

  /** Escape CSV cell (wrap in quotes if contains comma, quote, or newline) */
  const csvCell = (v: string | number): string => {
    const s = String(v ?? '')
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
  }

  const handlePrintList = async () => {
    if (!branchId) return
    setPrintListLoading(true)
    try {
      const res = await getSalaryByMonth(branchId, monthYear, 1, 10000)
      const list = res.data ?? []
      const uniqueEmpIds = [...new Set(list.map((r) => r.empId ?? r.employeeId).filter((id): id is number => typeof id === 'number' && id > 0))]
      const [departments, ...employeeResults] = await Promise.all([
        employeesService.getDepartmentLookups(),
        ...uniqueEmpIds.map((id) => employeesService.getEmployeeById(branchId, id).catch(() => null)),
      ])
      const empInfoMap = new Map<number, { departmentName: string; projectName: string }>()
      uniqueEmpIds.forEach((id, i) => {
        const emp = employeeResults[i] as any
        if (!emp) {
          empInfoMap.set(id, { departmentName: '—', projectName: '—' })
          return
        }
        const deptId = emp.empDepartmetId ?? emp.empDepartmentId
        const departmentName = deptId ? (departments.find((d) => d.id === deptId)?.lookupNameEn ?? emp.departmentName) ?? '—' : '—'
        const projectName = emp.projectName ?? emp.projectNameEn ?? '—'
        empInfoMap.set(id, { departmentName: departmentName || '—', projectName: projectName || '—' })
      })
      const branchName = user?.selectedBranch?.branchName ?? 'Branch'
      const dateLoc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
      const dateStr = new Date().toLocaleDateString(dateLoc, { month: 'numeric', day: '2-digit', year: '2-digit' })
      const timeStr = new Date().toLocaleTimeString(dateLoc, { hour: 'numeric', minute: '2-digit', hour12: true })
      const monthLabel = formatMonthYearLabel(monthYear, locale)
      const primaryColor = 'hsl(225, 46%, 16%)'
      const thStyle = `background:${primaryColor};color:white;padding:6px 8px;text-align:left;font-size:11px;font-weight:600;border:1px solid #e5e7eb;`
      const tdStyle = 'padding:6px 8px;font-size:11px;border:1px solid #e5e7eb;'
      const rows = list.map((row: SalaryByMonthItem) => {
        const name = getEmployeeNameForRow(row)
        const empId = row.empId ?? row.employeeId
        const info = empId ? empInfoMap.get(empId) : null
        const dept = info?.departmentName ?? (row as any).department ?? '—'
        const proj = info?.projectName ?? (row as any).project ?? '—'
        const raw = row as any
        const basic = raw.basicSalary ?? '—'
        const housing = raw.housingAllowance ?? '—'
        const transport = raw.transpotationAllowance ?? raw.transportationAllowance ?? '—'
        const meal = raw.mealAllowance ?? '—'
        const h = typeof raw.housingAllowance === 'number' ? raw.housingAllowance : 0
        const transportAmt = typeof raw.transpotationAllowance === 'number' ? raw.transpotationAllowance : (typeof raw.transportationAllowance === 'number' ? raw.transportationAllowance : 0)
        const m = typeof raw.mealAllowance === 'number' ? raw.mealAllowance : 0
        const o = typeof raw.otherAllowance === 'number' ? raw.otherAllowance : 0
        const totalAll = raw.totalAllowance ?? (h + transportAmt + m + o > 0 ? h + transportAmt + m + o : '—')
        const gosi = raw.gosiDeduction ?? raw.gosi ?? '—'
        const totalDed = raw.totalDeduction ?? '—'
        const net = row.totalSalary ?? '—'
        const num = (v: any) => (typeof v === 'number' ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v === '—' ? '—' : String(v))
        return `<tr>
          <td style="${tdStyle}">${row.empCode ?? `EMP-${row.empId ?? ''}`}</td>
          <td style="${tdStyle}">${name}</td>
          <td style="${tdStyle}">${dept}</td>
          <td style="${tdStyle}">${proj}</td>
          <td style="${tdStyle}">${monthLabel}</td>
          <td style="${tdStyle}">${num(basic)}</td>
          <td style="${tdStyle}">${num(housing)}</td>
          <td style="${tdStyle}">${num(transport)}</td>
          <td style="${tdStyle}">${num(meal)}</td>
          <td style="${tdStyle}">${num(totalAll)}</td>
          <td style="${tdStyle}">${num(gosi)}</td>
          <td style="${tdStyle}">${num(totalDed)}</td>
          <td style="${tdStyle}">${num(net)}</td>
        </tr>`
      }).join('')
      const win = window.open('', '_blank')
      if (!win) {
        toast.error(t('salaryDetail.toastPopupBlocked'))
        return
      }
      win.document.write(`
        <!DOCTYPE html><html><head>
          <title>${t('salaryDetail.printReportTitle')}</title>
          <style>
            body { font-family: system-ui, sans-serif; padding: 12px; color: #1f2937; margin: 0; font-size: 12px; }
            table { border-collapse: collapse; width: 100%; }
            .header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
            .header-date { font-size: 11px; color: #6b7280; }
            .header-branch { flex: 1; text-align: center; font-weight: 600; font-size: 14px; }
            .header-logo img { height: 32px; object-fit: contain; }
            .report-title { text-align: center; font-size: 18px; font-weight: 700; margin: 12px 0 8px; }
            .header-line { height: 3px; background: ${primaryColor}; margin: 8px 0 16px; }
            @media print { body { padding: 0; } }
          </style>
        </head><body>
          <div class="header-row">
            <div class="header-date">${dateStr}, ${timeStr}</div>
            <div class="header-branch">${branchName}</div>
            <div class="header-logo"><img src="${window.location.origin}${getLogoPath()}" alt="Logo" /></div>
          </div>
          <div class="report-title">${t('salaryDetail.printReportTitle')}</div>
          <div style="text-align:center;font-size:11px;margin-bottom:12px;">${t('salaryDetail.printReportRange', { from: monthLabel, to: monthLabel })}</div>
          <div class="header-line"></div>
          <table>
            <thead>
              <tr>
                <th style="${thStyle}">${t('salaryDetail.printColEmpCode')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColEmployee')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColDepartment')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColProject')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColMonthYear')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColBasic')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColHousing')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColTransport')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColMeal')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColTotalAll')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColGosi')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColTotalDed')}</th>
                <th style="${thStyle}">${t('salaryDetail.printColNetSalary')}</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body></html>
      `)
      win.document.close()
      win.focus()
      setTimeout(() => { win.print(); win.close() }, 300)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('salaryDetail.toastPrintFailed'))
    } finally {
      setPrintListLoading(false)
    }
  }

  const handleExcelList = async () => {
    if (!branchId) return
    setExcelListLoading(true)
    try {
      const res = await getSalaryByMonth(branchId, monthYear, 1, 10000)
      const list = res.data ?? []
      const uniqueEmpIds = [...new Set(list.map((r) => r.empId ?? r.employeeId).filter((id): id is number => typeof id === 'number' && id > 0))]
      const [departments, ...employeeResults] = await Promise.all([
        employeesService.getDepartmentLookups(),
        ...uniqueEmpIds.map((id) => employeesService.getEmployeeById(branchId, id).catch(() => null)),
      ])
      const empInfoMap = new Map<number, { departmentName: string; projectName: string }>()
      uniqueEmpIds.forEach((id, i) => {
        const emp = employeeResults[i] as any
        if (!emp) {
          empInfoMap.set(id, { departmentName: '—', projectName: '—' })
          return
        }
        const deptId = emp.empDepartmetId ?? emp.empDepartmentId
        const departmentName = deptId ? (departments.find((d) => d.id === deptId)?.lookupNameEn ?? emp.departmentName) ?? '—' : '—'
        const projectName = emp.projectName ?? emp.projectNameEn ?? '—'
        empInfoMap.set(id, { departmentName: departmentName || '—', projectName: projectName || '—' })
      })
      const monthLabel = formatMonthYearLabel(monthYear, locale)
      const num = (v: any) => (typeof v === 'number' ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v === '—' ? '—' : String(v ?? '—'))
      const headers = [
        t('salaryDetail.printColEmpCode'),
        t('salaryDetail.printColEmployee'),
        t('salaryDetail.printColDepartment'),
        t('salaryDetail.printColProject'),
        t('salaryDetail.printColMonthYear'),
        t('salaryDetail.printColBasic'),
        t('salaryDetail.printColHousing'),
        t('salaryDetail.printColTransport'),
        t('salaryDetail.printColMeal'),
        t('salaryDetail.printColTotalAll'),
        t('salaryDetail.printColGosi'),
        t('salaryDetail.printColTotalDed'),
        t('salaryDetail.printColNetSalary'),
      ]
      const rows = list.map((row: SalaryByMonthItem) => {
        const name = getEmployeeNameForRow(row)
        const empId = row.empId ?? row.employeeId
        const info = empId ? empInfoMap.get(empId) : null
        const dept = info?.departmentName ?? (row as any).department ?? '—'
        const proj = info?.projectName ?? (row as any).project ?? '—'
        const raw = row as any
        const basic = raw.basicSalary ?? '—'
        const housing = raw.housingAllowance ?? '—'
        const transport = raw.transpotationAllowance ?? raw.transportationAllowance ?? '—'
        const meal = raw.mealAllowance ?? '—'
        const h = typeof raw.housingAllowance === 'number' ? raw.housingAllowance : 0
        const transportAmt = typeof raw.transpotationAllowance === 'number' ? raw.transpotationAllowance : (typeof raw.transportationAllowance === 'number' ? raw.transportationAllowance : 0)
        const m = typeof raw.mealAllowance === 'number' ? raw.mealAllowance : 0
        const o = typeof raw.otherAllowance === 'number' ? raw.otherAllowance : 0
        const totalAll = raw.totalAllowance ?? (h + transportAmt + m + o > 0 ? h + transportAmt + m + o : '—')
        const gosi = raw.gosiDeduction ?? raw.gosi ?? '—'
        const totalDed = raw.totalDeduction ?? '—'
        const net = row.totalSalary ?? '—'
        return [
          csvCell(row.empCode ?? `EMP-${row.empId ?? ''}`),
          csvCell(name),
          csvCell(dept),
          csvCell(proj),
          csvCell(monthLabel),
          csvCell(num(basic)),
          csvCell(num(housing)),
          csvCell(num(transport)),
          csvCell(num(meal)),
          csvCell(num(totalAll)),
          csvCell(num(gosi)),
          csvCell(num(totalDed)),
          csvCell(num(net)),
        ].join(',')
      })
      const csv = [headers.join(','), ...rows].join('\r\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${t('salaryDetail.excelFileNamePrefix')}_${monthYear || 'export'}.csv`
      a.click()
      URL.revokeObjectURL(url)
      toast.success(t('salaryDetail.toastExcelSuccess'))
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('salaryDetail.toastExcelFailed'))
    } finally {
      setExcelListLoading(false)
    }
  }

  const openPaySlip = async (row: SalaryByMonthItem) => {
    const id = row.id
    if (id == null) {
      toast.error(t('salaryDetail.toastSalaryIdMissing'))
      return
    }
    setPaySlipLoading(true)
    setPaySlipData(null)
    setPaySlipOpen(true)
    try {
      const detail = await getSalaryById(id)
      setPaySlipData(detail)
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('salaryDetail.toastSalaryDetailFailed'))
      setPaySlipOpen(false)
    } finally {
      setPaySlipLoading(false)
    }
  }

  useEffect(() => {
    if (!branchId) return
    contractService.getAllEmployees(branchId).then((data) => setEmployees(data ?? [])).catch(() => setEmployees([]))
  }, [branchId])

  const loadSalaryByMonth = async () => {
    if (!branchId) return
    setLoading(true)
    try {
      const res = await getSalaryByMonth(branchId, monthYear, pageNumber, pageSize)
      setSalaryList(res.data ?? [])
      setTotalRecords(res.totalRecords ?? 0)
    } catch (e: any) {
      setSalaryList([])
      setTotalRecords(0)
      const status = e?.response?.status
      const msg = (e?.response?.data?.message ?? e?.message ?? '').toLowerCase()
      const isNoData = status === 404 || /no data|not found|no records|no salary/.test(msg)
      if (!isNoData) {
        toast.error(e?.response?.data?.message ?? t('salaryDetail.toastSalaryListFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  // When month/year changes, sync start/end dates to that month's range (user can still edit them)
  useEffect(() => {
    setStartDate(defaultRange.startDate)
    setEndDate(defaultRange.endDate)
  }, [defaultRange.startDate, defaultRange.endDate])

  // When month/year or branch changes: reset test state and go to page 1
  useEffect(() => {
    if (!branchId) return
    setTestList([])
    setTested(false)
    setPageNumber(1)
  }, [branchId, monthYear])

  // Get Salary API: on branch, month/year, or pagination change
  useEffect(() => {
    if (!branchId) {
      setSalaryList([])
      setTotalRecords(0)
      setTestList([])
      setTested(false)
      return
    }
    loadSalaryByMonth()
  }, [branchId, monthYear, pageNumber, pageSize])

  const handleTestPayroll = async () => {
    if (!branchId) return
    setTestLoading(true)
    try {
      const res = await getPayrollTest({
        branchId,
        monthYear,
        startDate,
        endDate,
        empId: 0,
        pageNumber: 1,
        pageSize: 100,
      })
      setTestList(res.data ?? [])
      setTestTotalRecords(res.totalRecords ?? 0)
      setTested(true)
      toast.success(t('salaryDetail.toastTestPayrollSuccess'))
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('salaryDetail.toastTestPayrollFailed'))
    } finally {
      setTestLoading(false)
    }
  }

  const handlePostPayroll = async () => {
    if (!branchId) return
    setPosting(true)
    try {
      const payload: GeneratePayrollPayload = {
        monthYear: toApiMonthYear(monthYear),
        startDate: new Date(startDate).toISOString?.() ?? startDate,
        endDate: new Date(endDate).toISOString?.() ?? endDate,
        branchId,
      }
      console.log('Generate Payroll (POST) Payload:', JSON.stringify(payload, null, 2))
      await generatePayroll(payload)
      await showSuccess(t('salaryDetail.postPayrollSuccessTitle'), t('salaryDetail.postPayrollSuccessMessage'))
      setTested(false)
      setTestList([])
      await loadSalaryByMonth()
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? t('salaryDetail.toastGenerateFailed'))
    } finally {
      setPosting(false)
    }
  }

  const getEmployeeNameForRow = (row: SalaryByMonthItem): string => {
    if (row.employeeName) return row.employeeName
    const empId = row.empId ?? row.employeeId
    if (empId && employees.length > 0) {
      const emp = employees.find((e) => e.empId === empId)
      if (emp) {
        return emp.fullName ?? ([emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ').trim() || t('salaryDetail.employeeFallback', { id: empId }))
      }
    }
    return row.empCode ? String(row.empCode) : '—'
  }

  const filteredSalaryRows = useMemo(() => {
    if (!search.trim()) return salaryList
    const q = search.toLowerCase()
    return salaryList.filter((r) => {
      const displayName = getEmployeeNameForRow(r)
      const monthYearDisplay = formatMonthYearLabel(r.monthYear ?? r.salaryMonthYear, locale)
      return (
        displayName.toLowerCase().includes(q) ||
        (r.empCode ?? '').toLowerCase().includes(q) ||
        (r.monthYear ?? '').toLowerCase().includes(q) ||
        (r.salaryMonthYear ?? '').toLowerCase().includes(q) ||
        monthYearDisplay.toLowerCase().includes(q)
      )
    })
  }, [salaryList, search, employees, locale, t])

  const filteredTestRows = useMemo(() => {
    if (!search.trim()) return testList
    const q = search.toLowerCase()
    return testList.filter(
      (r) =>
        (r.employeeName ?? '').toLowerCase().includes(q) ||
        (r.empCode ?? '').toLowerCase().includes(q) ||
        (r.department ?? '').toLowerCase().includes(q)
    )
  }, [testList, search])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('salaryDetail.pageTitle')} description={t('salaryDetail.pageDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('salaryDetail.selectBranch')}
          </CardContent>
        </Card>
      </div>
    )
  }

  const showSalaryTable = hasSalaryData && !tested
  const showTestTable = tested && testList.length > 0

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('salaryDetail.pageTitle')} description={t('salaryDetail.pageDescription')}>
        <div className="flex items-center gap-2">
          {!hasSalaryData && !tested && (
            <Button
              variant="default"
              disabled={testLoading}
              onClick={handleTestPayroll}
            >
              {testLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('salaryDetail.testPayroll')}
            </Button>
          )}
          {tested && (
            <Button
              variant="default"
              disabled={posting}
              onClick={handlePostPayroll}
            >
              {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {t('salaryDetail.postPayroll')}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handlePrintList} disabled={printListLoading}>
            {printListLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
            {t('salaryDetail.print')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExcelList} disabled={excelListLoading}>
            {excelListLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            {t('salaryDetail.excel')}
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>{t('salaryDetail.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('salaryDetail.labelRequired')} {t('salaryDetail.labelMonthYear')}</label>
              <Input
                type="month"
                value={monthYear}
                onChange={(e) => setMonthYear(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('salaryDetail.labelRequired')} {t('salaryDetail.labelStartDate')}</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('salaryDetail.labelRequired')} {t('salaryDetail.labelEndDate')}</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full" />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('salaryDetail.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                {t('salaryDetail.csv')}
              </Button>
              <Button variant="outline" size="sm">
                <Copy className="w-4 h-4 mr-2" />
                {t('salaryDetail.copy')}
              </Button>
            </div>
          </div>

          <div className="rounded-md border overflow-x-auto">
            {loading && !tested ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : showSalaryTable ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center">{t('salaryDetail.columnEmployee')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.columnMonthYear')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.columnTotalSalary')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.columnPayrollDate')}</TableHead>
                    <TableHead className="text-center w-[80px]">{t('salaryDetail.columnAction')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSalaryRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        {t('salaryDetail.noData')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSalaryRows.map((row, idx) => (
                      <TableRow key={row.id ?? row.empId ?? row.employeeId ?? idx}>
                        <TableCell className="text-center">{getEmployeeNameForRow(row)}</TableCell>
                        <TableCell className="text-center">{formatMonthYearLabel(row.monthYear ?? row.salaryMonthYear, locale)}</TableCell>
                        <TableCell className="text-center">{formatNum(row.totalSalary)}</TableCell>
                        <TableCell className="text-center">{formatDate(row.payrollDate, locale)}</TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                            onClick={() => openPaySlip(row)}
                            disabled={paySlipLoading}
                            title={t('salaryDetail.viewPaySlipTitle')}
                          >
                            <ListOrdered className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            ) : showTestTable ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center">{t('salaryDetail.testColEmpCode')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColEmpRef')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColEmployee')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColDepartment')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColProject')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColMonthYear')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColBasic')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColHousing')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColTransport')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColMeal')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColOther')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColTotalAllowance')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColGross')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColOtAmount')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColGosi')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColLateDeduction')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColEarlyOutDeduction')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColTotalDeduction')}</TableHead>
                    <TableHead className="text-center">{t('salaryDetail.testColNetSalary')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTestRows.map((row, idx) => (
                    <TableRow key={row.id ?? row.empId ?? idx}>
                      <TableCell className="text-center">{row.empCode ?? '—'}</TableCell>
                      <TableCell className="text-center">{row.empRef ?? '—'}</TableCell>
                      <TableCell className="text-center">{row.employeeName ?? '—'}</TableCell>
                      <TableCell className="text-center">{row.department ?? '—'}</TableCell>
                      <TableCell className="text-center">{row.project ?? '—'}</TableCell>
                      <TableCell className="text-center">{formatMonthYearLabel(row.monthYear?.replace(/^(\d{2})-(\d{4})$/, '$2-$1'), locale)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.basicSalary)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.housingAllowance)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.transportationAllowance)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.mealAllowance)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.otherAllowance)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.totalAllowance)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.grossSalary)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.otAmount)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.gosi)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.lateDeduction)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.earlyOutDeduction)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.totalDeduction)}</TableCell>
                      <TableCell className="text-center">{formatNum(row.netSalary)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="py-12 text-center text-muted-foreground">
                {t('salaryDetail.noData')}
              </div>
            )}
          </div>

          {showSalaryTable && (
            <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
              <span>
                {t('salaryDetail.paginationShowing', {
                  from: totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1,
                  to: totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords),
                  total: totalRecords,
                })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                >
                  {t('salaryDetail.previous')}
                </Button>
                <span>{t('salaryDetail.pageOf', { page: pageNumber, totalPages })}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNumber >= totalPages}
                  onClick={() => setPageNumber((p) => p + 1)}
                >
                  {t('salaryDetail.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <PaySlipModal
        open={paySlipOpen}
        onOpenChange={setPaySlipOpen}
        data={paySlipData}
        loading={paySlipLoading}
      />
    </div>
  )
}
